import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { SECTION_COLOR, CERVENA, Nadpis } from './ui';

/**
 * Průběh objednávky z knihy událostí (návrh „Stopa objednávky“, E5).
 *
 * Vedoucí a admin dostanou ze serveru celý průběh — kdo co udělal, za koho
 * (manažer směny, když pokladna jela bez přihlášení), odkud a co se změnilo.
 * Ostatní jen milníky bez jmen. Server rozhoduje (`plny`), tady se jen kreslí.
 */
export default function PrubehObjednavky({ id, obnova = 0, sekce, muted, isDark }) {
    const [data, setData] = useState(null);
    const [vse, setVse] = useState(false);

    useEffect(() => {
        let platne = true;
        fetch(`/api/objednavky/${id}/udalosti`, { headers: { Accept: 'application/json' } })
            .then(r => (r.ok ? r.json() : null))
            .then(d => { if (platne) setData(d); })
            .catch(() => { if (platne) setData(null); });
        return () => { platne = false; };
    }, [id, obnova]);

    if (data === null) {
        return (
            <div style={sekce}>
                <Nadpis muted={muted}>Průběh</Nadpis>
                <Loader2 size={16} className="animate-spin" style={{ color: muted }} />
            </div>
        );
    }
    // Starší objednávky (před knihou událostí) — sekci vůbec neukazovat.
    if (!data.udalosti?.length) return null;

    const radky = data.plny && !vse ? data.udalosti.filter(u => u.milnik || u.zaporne || u.druh.startsWith('polozka_')) : data.udalosti;
    const skryto = data.udalosti.length - radky.length;
    const sedy = isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.2)';

    return (
        <div style={sekce}>
            <div className="flex items-baseline justify-between" style={{ gap: 10 }}>
                <Nadpis muted={muted}>Průběh</Nadpis>
                {data.provozni_den && (
                    <span style={{ fontSize: 11.5, color: muted }}>provozní den {fmtDen(data.provozni_den.datum)}</span>
                )}
            </div>
            <div className="flex flex-col">
                {radky.map((u, i) => {
                    const barva = u.zaporne ? CERVENA : u.milnik ? SECTION_COLOR : sedy;
                    const kdo = u.provedl || (u.odpovida ? `za ${u.odpovida}` : null);
                    const odkud = [u.zdroj, u.zarizeni].filter(Boolean).join(' · ');
                    return (
                        <div key={u.id} className="flex" style={{ gap: 10, paddingBottom: 8, position: 'relative' }}>
                            {/* Svislá osa */}
                            {i < radky.length - 1 && (
                                <span aria-hidden="true" style={{ position: 'absolute', left: 61, top: 14, bottom: -2, width: 1, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />
                            )}
                            <span style={{ width: 48, flexShrink: 0, fontSize: 12, color: muted, fontVariantNumeric: 'tabular-nums', paddingTop: 1 }}>
                                {cas(u.kdy, data.plny)}
                            </span>
                            <span style={{ width: 9, height: 9, borderRadius: 9, flexShrink: 0, marginTop: 5, background: barva, position: 'relative' }} />
                            <div className="min-w-0 flex-1">
                                <div style={{ fontSize: 13, color: u.zaporne ? CERVENA : 'var(--text-primary)', fontWeight: u.milnik ? 600 : 400 }}>
                                    {u.popis}
                                </div>
                                {u.detail && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{u.detail}</div>}
                                {(kdo || odkud) && (
                                    <div style={{ fontSize: 11.5, color: muted }}>
                                        {[kdo, odkud].filter(Boolean).join(' · ')}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {data.plny && (skryto > 0 || vse) && (
                <button type="button" onClick={() => setVse(v => !v)}
                    style={{ marginTop: 2, minHeight: 36, padding: '0 2px', fontSize: 12.5, fontWeight: 600, color: SECTION_COLOR, background: 'transparent', border: 'none', cursor: 'pointer' }}>
                    {vse ? 'Jen hlavní kroky' : `Ukázat vše (+${skryto})`}
                </button>
            )}
        </div>
    );
}

// Plný průběh se čte na sekundy (kdo dřív ťukl), krátký stačí na minuty.
function cas(iso, sekundy) {
    const d = new Date(iso);
    return d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit', ...(sekundy ? { second: '2-digit' } : {}) });
}

function fmtDen(iso) {
    return new Date(`${iso}T12:00:00`).toLocaleDateString('cs-CZ', { weekday: 'short', day: 'numeric', month: 'numeric' });
}
